import type { OverrideProperty } from '@bindoc-projects/util-domain-lib';
import type {
  ShareableType} from '@bindoc-projects/resource-domain-lib';
import {
  BdChildResourceHelper
} from '@bindoc-projects/resource-domain-lib';
import { VirtualClinicAnalysisOperatingNumberTerm } from './term';
import { VirtualClinicAnalysisOperatingNumberTermFactory } from './term-factory';
import type { VirtualClinicAnalysisFullyInpatientCasesFilter } from '../filter/fully-inpatient-cases';
import type { FilterRegistrationToken } from '@bindoc-projects/filter-condition-domain-lib';
import type { VirtualClinicAnalysisOperatingNumberFunctionTermSpec } from '../../resolving';
import type { ArithmeticOperator } from '@bindoc-projects/util-domain-lib';
import { isCalculationTermComplete } from '@bindoc-projects/util-domain-lib';

/**
 * How two terms are combined.
 *
 * Aliased to the shared `ArithmeticOperator` (BDA-5849): both cubes accept the
 * same four operations, so the union has one definition. The name is kept so
 * no consumer import had to change.
 */
export type VirtualClinicAnalysisOperatingNumberOperation = ArithmeticOperator;

export class VirtualClinicAnalysisOperatingNumberFunction extends VirtualClinicAnalysisOperatingNumberTerm {
  public static async fromObject(
    obj: {
      leftOperand?: unknown;
      rightOperand?: unknown;
      operation?: VirtualClinicAnalysisOperatingNumberOperation;
    } = {},
    registeredFilter: FilterRegistrationToken[],
  ): Promise<VirtualClinicAnalysisOperatingNumberFunction> {
    const leftOperand =
      await VirtualClinicAnalysisOperatingNumberTermFactory.fromObject(
        obj.leftOperand,
        registeredFilter,
      );
    const rightOperand =
      await VirtualClinicAnalysisOperatingNumberTermFactory.fromObject(
        obj.rightOperand,
        registeredFilter,
      );

    return new VirtualClinicAnalysisOperatingNumberFunction(
      leftOperand,
      rightOperand,
      obj.operation,
    );
  }

  constructor(
    public leftOperand: VirtualClinicAnalysisOperatingNumberTerm,
    public rightOperand: VirtualClinicAnalysisOperatingNumberTerm,
    public operation: VirtualClinicAnalysisOperatingNumberOperation,
  ) {
    super('function');
  }

  clone(): VirtualClinicAnalysisOperatingNumberTerm {
    return new VirtualClinicAnalysisOperatingNumberFunction(
      this.leftOperand ? this.leftOperand.clone() : null,
      this.rightOperand ? this.rightOperand.clone() : null,
      this.operation,
    );
  }

  overrideClone(
    override: OverrideProperty[],
  ): VirtualClinicAnalysisOperatingNumberTerm {
    return new VirtualClinicAnalysisOperatingNumberFunction(
      this.leftOperand.overrideClone(override),
      this.rightOperand.overrideClone(override),
      this.operation,
    );
  }

  getChildResources(): { [resourceName in ShareableType]?: string[] } {
    return BdChildResourceHelper.merge(
      this.leftOperand.getChildResources(),
      this.rightOperand.getChildResources(),
    );
  }

  getFullyInpatientCasesFilters(): VirtualClinicAnalysisFullyInpatientCasesFilter[] {
    const fullyInpatientCasesFilters: VirtualClinicAnalysisFullyInpatientCasesFilter[] =
      [];

    if (this.leftOperand) {
      fullyInpatientCasesFilters.push(
        ...this.leftOperand.getFullyInpatientCasesFilters(),
      );
    }

    if (this.rightOperand) {
      fullyInpatientCasesFilters.push(
        ...this.rightOperand.getFullyInpatientCasesFilters(),
      );
    }

    return fullyInpatientCasesFilters;
  }

  getOverrideProperties(
    tileId: string,
    dependencyVirtualClinicId: string,
  ): OverrideProperty[] {
    return [
      ...this.leftOperand.getOverrideProperties(
        tileId,
        dependencyVirtualClinicId,
      ),
      ...this.rightOperand.getOverrideProperties(
        tileId,
        dependencyVirtualClinicId,
      ),
    ];
  }

  isValid(): boolean {
    // The recursion goes back through isValid() rather than through the shared
    // rule's own default, so an operand that is a fully-inpatient-case leaf
    // still gets asked about its case filter.
    return isCalculationTermComplete<VirtualClinicAnalysisOperatingNumberTerm>(
      this,
      (operand) => operand.isValid(),
    );
  }

  toJSON(): VirtualClinicAnalysisOperatingNumberFunctionTermSpec {
    return {
      type: 'function',
      leftOperand: this.leftOperand ? this.leftOperand.toJSON() : null,
      rightOperand: this.rightOperand ? this.rightOperand.toJSON() : null,
      operation: this.operation,
    };
  }

  onUpdateVirtualClinicId(virtualClinicId: string): void {
    this.leftOperand.onUpdateVirtualClinicId(virtualClinicId);
    this.rightOperand.onUpdateVirtualClinicId(virtualClinicId);
  }
}
